/**
 * Batch company-name enrichment for ops-console customer cells (#3040).
 *
 * Cells subscribe per id through React Query. A module-level loader keyed
 * by `fetchImpl` coalesces those subscriptions: the first queued id arms a
 * single `setTimeout(flush, 10)`, and the flush de-dupes, splits into
 * chunks of at most `CUSTOMER_COMPANY_LOOKUP_MAX_IDS` (org ids + account
 * ids together), and POSTs each chunk to
 * `/v1/ops/directory/customer-companies`. Every waiter of a chunk resolves
 * with that id's `company`, or rejects when that chunk's request fails.
 *
 * A failed lookup must never break a cell — callers render
 * `status: 'unavailable'` by omitting the company line and keeping the
 * identity that was already on screen.
 */
import {
  CUSTOMER_COMPANY_LOOKUP_MAX_ID_LENGTH,
  CUSTOMER_COMPANY_LOOKUP_MAX_IDS,
  type CustomerCompanyLookupRequest,
  type CustomerCompanyLookupResponse,
} from '@nadoutong/contracts';
import { useQuery, type QueryClient, type UseQueryResult } from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import { opsApiRequest } from './client.ts';

export const CUSTOMER_COMPANY_QUERY_ROOT = ['customer-company'] as const;

const STALE_TIME_MS = 5 * 60_000;
const FLUSH_DELAY_MS = 10;
const LOOKUP_PATH = '/v1/ops/directory/customer-companies';

type CompanyKind = 'org' | 'account';

interface QueuedLookup {
  readonly kind: CompanyKind;
  readonly id: string;
  readonly resolve: (company: string | null) => void;
  readonly reject: (error: unknown) => void;
}

interface CompanyLoader {
  load(kind: CompanyKind, id: string): Promise<string | null>;
}

const loaders = new WeakMap<typeof fetch, CompanyLoader>();

function companyById(
  response: CustomerCompanyLookupResponse,
  kind: CompanyKind,
): ReadonlyMap<string, string | null> {
  const byId = new Map<string, string | null>();
  if (kind === 'org') {
    for (const entry of response.orgs) {
      byId.set(entry.org_id, entry.company);
    }
    return byId;
  }
  for (const entry of response.accounts) {
    byId.set(entry.account_id, entry.company);
  }
  return byId;
}

function createLoader(fetchImpl: typeof fetch): CompanyLoader {
  let queue: QueuedLookup[] = [];
  let flushTimer: ReturnType<typeof setTimeout> | null = null;

  function schedule(): void {
    if (flushTimer !== null) {
      return;
    }
    flushTimer = setTimeout(() => {
      flushTimer = null;
      void flush();
    }, FLUSH_DELAY_MS);
  }

  async function postChunk(chunk: readonly QueuedLookup[]): Promise<void> {
    const orgIds: string[] = [];
    const accountIds: string[] = [];
    const seenOrg = new Set<string>();
    const seenAccount = new Set<string>();
    for (const item of chunk) {
      if (item.kind === 'org') {
        if (!seenOrg.has(item.id)) {
          seenOrg.add(item.id);
          orgIds.push(item.id);
        }
      } else if (!seenAccount.has(item.id)) {
        seenAccount.add(item.id);
        accountIds.push(item.id);
      }
    }
    const body: CustomerCompanyLookupRequest = {
      ...(orgIds.length > 0 ? { org_ids: orgIds } : {}),
      ...(accountIds.length > 0 ? { account_ids: accountIds } : {}),
    };
    if (body.org_ids === undefined && body.account_ids === undefined) {
      const error = new Error('customer company lookup chunk was empty');
      for (const item of chunk) {
        item.reject(error);
      }
      return;
    }
    try {
      const response = await opsApiRequest<CustomerCompanyLookupResponse>(
        LOOKUP_PATH,
        { method: 'POST', body },
        fetchImpl,
      );
      const orgs = companyById(response, 'org');
      const accounts = companyById(response, 'account');
      for (const item of chunk) {
        const map = item.kind === 'org' ? orgs : accounts;
        if (!map.has(item.id)) {
          item.reject(new Error(`customer company lookup omitted ${item.kind} ${item.id}`));
          continue;
        }
        const found = map.get(item.id);
        item.resolve(found ?? null);
      }
    } catch (error) {
      for (const item of chunk) {
        item.reject(error);
      }
    }
  }

  async function flush(): Promise<void> {
    const pending = queue;
    queue = [];
    if (pending.length === 0) {
      return;
    }
    const chunks: QueuedLookup[][] = [];
    let current: QueuedLookup[] = [];
    let currentSize = 0;
    const seenInChunk = new Set<string>();
    for (const item of pending) {
      const key = `${item.kind}:${item.id}`;
      const isNew = !seenInChunk.has(key);
      if (isNew && currentSize >= CUSTOMER_COMPANY_LOOKUP_MAX_IDS) {
        chunks.push(current);
        current = [];
        currentSize = 0;
        seenInChunk.clear();
      }
      if (!seenInChunk.has(key)) {
        seenInChunk.add(key);
        currentSize += 1;
      }
      current.push(item);
    }
    if (current.length > 0) {
      chunks.push(current);
    }
    await Promise.all(chunks.map((chunk) => postChunk(chunk)));
  }

  return {
    load(kind, id) {
      return new Promise<string | null>((resolve, reject) => {
        queue.push({ kind, id, resolve, reject });
        schedule();
      });
    },
  };
}

function loaderFor(fetchImpl: typeof fetch): CompanyLoader {
  const existing = loaders.get(fetchImpl);
  if (existing !== undefined) {
    return existing;
  }
  const created = createLoader(fetchImpl);
  loaders.set(fetchImpl, created);
  return created;
}

export type CustomerCompanyState =
  | { readonly status: 'idle' | 'loading' | 'unavailable' }
  | { readonly status: 'resolved'; readonly company: string | null };

function stateFromQuery(query: UseQueryResult<string | null>): CustomerCompanyState {
  // A failed background refetch keeps the last good company.
  if (query.data !== undefined) {
    return { status: 'resolved', company: query.data };
  }
  if (query.isError) {
    return { status: 'unavailable' };
  }
  if (query.fetchStatus === 'idle' && query.status === 'pending') {
    return { status: 'idle' };
  }
  return { status: 'loading' };
}

/**
 * Company name for one customer cell. `orgId` wins when both ids are
 * given. A blank id, whitespace, or an id longer than
 * `CUSTOMER_COMPANY_LOOKUP_MAX_ID_LENGTH` stays `idle` and is never
 * queued — the lookup endpoint rejects those. An org id that was given
 * but rejected does not fall through to the account id.
 */
function acceptedId(raw: string | null | undefined): string {
  const trimmed = raw?.trim() ?? '';
  if (trimmed.length === 0 || trimmed.length > CUSTOMER_COMPANY_LOOKUP_MAX_ID_LENGTH) {
    return '';
  }
  return trimmed;
}

function orgIdWasGiven(raw: string | null | undefined): boolean {
  return raw !== undefined && raw !== null && raw.trim().length > 0;
}

export function useCustomerCompany(input: {
  readonly orgId?: string | null;
  readonly accountId?: string | null;
}): CustomerCompanyState {
  const { fetchImpl, status } = useSession();
  const orgGiven = orgIdWasGiven(input.orgId);
  const orgId = acceptedId(input.orgId);
  const accountId = orgGiven ? '' : acceptedId(input.accountId);
  const kind: CompanyKind = orgId.length > 0 ? 'org' : 'account';
  const id = orgId.length > 0 ? orgId : accountId;
  const enabled = status === 'authenticated' && id.length > 0;
  const query = useQuery({
    queryKey:
      kind === 'org'
        ? ([...CUSTOMER_COMPANY_QUERY_ROOT, 'org', id] as const)
        : ([...CUSTOMER_COMPANY_QUERY_ROOT, 'account', id] as const),
    queryFn: () => loaderFor(fetchImpl).load(kind, id),
    enabled,
    staleTime: STALE_TIME_MS,
    retry: false,
  });
  if (!enabled) {
    return { status: 'idle' };
  }
  return stateFromQuery(query);
}

export function invalidateCustomerCompanies(qc: QueryClient): Promise<void> {
  return qc.invalidateQueries({ queryKey: CUSTOMER_COMPANY_QUERY_ROOT });
}
